import { createContext, useContext } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import './portal.css';
import { useAsync } from '@/lib/useAsync';
import { useAppStore, useIsStaff } from '@/app/store';
import { Spinner } from '@/components/ui/States';
import { api, type PortalConfig } from '@/lib/api';

/* The customer-facing shell. Branding comes from /portal/config (per-install
 * PORTAL_* env), applied as CSS variables so the same build wears each
 * client's identity. Staff can open it too (it is the demo surface). */

const ConfigCtx = createContext<PortalConfig | null>(null);
export const usePortalConfig = () => useContext(ConfigCtx)!;

const NAV = [
  { to: '/portal', label: 'The Hive', end: true },
  { to: '/portal/deliverables', label: 'Deliverables' },
  { to: '/portal/reports', label: 'Reports' },
];
const NAV_YOU = [{ to: '/portal/connect', label: 'Connect your AI' }];

export function PortalLayout() {
  const config = useAsync(() => api.getPortalConfig(), []);
  const user = useAppStore((s) => s.user);
  const isStaff = useIsStaff();

  if (config.status !== 'success') {
    return <div className="flex h-full items-center justify-center"><Spinner label="Loading…" /></div>;
  }
  const c = config.data;
  const vars = {
    '--p-bg': c.bg,
    '--p-bg2': c.bg2,
    '--p-accent': c.accent,
    '--p-accent-ink': c.accentInk,
  } as React.CSSProperties;

  return (
    <ConfigCtx.Provider value={c}>
      <div className="portal" style={vars}>
        <aside className="portal-side">
          <div className="portal-wordmark">
            {c.wordmarkA}
            <span className="b">{c.wordmarkB}</span>
          </div>
          <nav className="portal-nav">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => (isActive ? 'on' : '')}>
                {n.label}
              </NavLink>
            ))}
            <p className="sec">You</p>
            {NAV_YOU.map((n) => (
              <NavLink key={n.to} to={n.to} className={({ isActive }) => (isActive ? 'on' : '')}>
                {n.label}
              </NavLink>
            ))}
            {isStaff && (
              <>
                <p className="sec">Staff</p>
                <NavLink to="/">Operator console</NavLink>
              </>
            )}
          </nav>
          <div className="portal-foot">
            Signed in as <b>{user?.name ?? user?.email}</b>
            <br />
            <br />
            Powered by swarm
            <br />
            Supported by WarriorLab
          </div>
        </aside>
        <main className="portal-main">
          <Outlet />
        </main>
      </div>
    </ConfigCtx.Provider>
  );
}
